import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button, Panel, PanelSection, toast } from '@/shared/ui';
import { TextField } from '@/shared/forms';
import { useNavigator } from '@/shared/navigation/react/useNavigator';
import { useAgendaParams } from '@/shared/navigation/react/useAgendaParams';
import { plannerQueryKeys } from '../../plannerApi';
import { SerieFormFields } from '../../serie';
import { productFormAgenda } from './productFormAgenda';
import { useProductForm } from './useProductForm';

interface ProductFormPageProps {
  layerId: string;
}

/**
 * Formulář pro založení nového produktu. Nad sdílenými poli série přidává Název produktu;
 * při uložení vznikne produkt (legacy + Phoenix) i jeho první série.
 */
export function ProductFormPage({ layerId }: ProductFormPageProps) {
  const { t } = useTranslation('planner');
  const navigator = useNavigator();
  const queryClient = useQueryClient();
  const { open } = useAgendaParams(productFormAgenda, layerId);

  const closeForm = useCallback(() => {
    void navigator.closeLayer(layerId);
  }, [navigator, layerId]);

  const { form } = useProductForm({
    projectId: open.projectId,
    onSaved: () => {
      void queryClient.invalidateQueries({ queryKey: plannerQueryKeys.tree() });
      toast({ severity: 'success', description: t('product.toast.saved') });
      closeForm();
    },
    onError: () => {
      toast({
        severity: 'error',
        title: t('product.toast.errorTitle'),
        description: t('product.toast.saveError'),
      });
    },
  });

  return (
    <div className="bg-background h-full min-h-0 overflow-y-auto" data-testid="product-form">
      <div className="mx-auto w-full max-w-220 p-4">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void form.handleSubmit();
          }}
        >
          <Panel
            variant="filled"
            title={t('product.titleCreate')}
            footer={
              <div className="gap-phx-2 flex w-full items-center justify-end">
                <form.Subscribe
                  selector={(state) => ({
                    canSubmit: state.canSubmit,
                    isSubmitting: state.isSubmitting,
                  })}
                >
                  {({ canSubmit, isSubmitting }) => (
                    <>
                      <Button variant="ghost" size="md" type="button" onClick={closeForm}>
                        {t('product.actions.cancel')}
                      </Button>
                      <Button
                        variant="primary"
                        size="md"
                        type="submit"
                        loading={isSubmitting}
                        disabled={!canSubmit || isSubmitting}
                        data-testid="product-save-button"
                      >
                        {t('product.actions.save')}
                      </Button>
                    </>
                  )}
                </form.Subscribe>
              </div>
            }
          >
            <PanelSection title={t('product.name.sectionLabel')}>
              <form.Field name="productName">
                {(field) => (
                  <TextField
                    field={field}
                    label={t('product.name.label')}
                    required
                    helperText={t('product.name.help')}
                    placeholder={t('product.name.placeholder')}
                    data-testid="product-name-input"
                  />
                )}
              </form.Field>
            </PanelSection>

            <SerieFormFields form={form} />
          </Panel>
        </form>
      </div>
    </div>
  );
}
